import { useCallback } from 'react'
import { type FieldValues, useForm, type UseFormReturn, useFormState } from 'react-hook-form'

export type SaveResult = { ok: true } | { ok: false; error?: unknown }

export interface SyncedForm<V extends FieldValues> {
  form: UseFormReturn<V>
  /** Top-level fields the user changed that the server hasn't caught up with. */
  changedFields: Array<keyof V & string>
  isDirty: boolean
  /** Back to the server's values. */
  discard: () => void
  /** The current values become the saved ones. */
  markSaved: () => void
}

function hasDirtyEntry(entry: unknown): boolean {
  if (entry === true) return true
  if (Array.isArray(entry)) return entry.some(hasDirtyEntry)
  if (entry && typeof entry === 'object') return Object.values(entry).some(hasDirtyEntry)
  return false
}

/** A form that follows `values` (pass them memoised) as fresh data arrives, keeping the fields the user changed. */
export function useSyncedForm<V extends FieldValues>(values: V): SyncedForm<V> {
  const form = useForm<V>({ values, resetOptions: { keepDirtyValues: true } })
  // Reading dirtyFields subscribes to them; keepDirtyValues only keeps the edits it can see.
  const { dirtyFields } = useFormState({ control: form.control })
  const changedFields = Object.keys(dirtyFields).filter((key) =>
    hasDirtyEntry((dirtyFields as Record<string, unknown>)[key])
  ) as Array<keyof V & string>
  const { reset, getValues } = form
  const discard = useCallback(() => reset(values, { keepDirtyValues: false }), [reset, values])
  const markSaved = useCallback(() => reset(getValues(), { keepDirtyValues: false }), [reset, getValues])

  return { form, changedFields, isDirty: changedFields.length > 0, discard, markSaved }
}
